import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import LeadDetailModal from '@/components/LeadDetailModal';
import AppShell from '@/components/ops/AppShell';
import PipelineBoard from '@/components/ops/PipelineBoard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

export default function OpsInterviewer() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
    // Allow admin to view interviewer page too.
    if (!user.roles?.includes('interviewer') && !user.roles?.includes('admin')) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, stagesRes] = await Promise.all([
        axios.get(`${API}/ops/leads`, getAuth()),
        axios.get(`${API}/ops/stages`, getAuth()),
      ]);
      setLeads(leadsRes.data);
      setStages(stagesRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  if (authLoading || loading) {
    return (
      <div className="ops-shell min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const isAdmin = user?.roles?.includes('admin');
  const subtitle = stages.length
    ? `${leads.length} lead${leads.length === 1 ? '' : 's'} across ${stages.length} stages${isAdmin ? ' · all interviewers' : ''}`
    : `${leads.length} leads`;

  return (
    <AppShell
      pageTitle="Pipeline"
      subtitle={subtitle}
      activeNav="pipeline"
      onNavigate={() => {}}
      role={isAdmin ? 'admin' : 'interviewer'}
    >
      <div data-testid="ops-interviewer-page" className="h-full">
        {leads.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto max-w-md rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10">
              <p className="text-[14px] text-slate-900 font-medium mb-1">{isAdmin ? 'No leads in the pipeline yet' : 'No leads assigned yet'}</p>
              <p className="text-[12px] text-slate-500">
                {isAdmin
                  ? 'When interviewers receive leads, the pipeline will populate here.'
                  : 'Your admin will assign leads to you. They will appear here, grouped by pipeline stage.'}
              </p>
            </div>
          </div>
        ) : (
          <PipelineBoard
            stages={stages}
            leads={leads}
            onCardClick={(id) => setSelectedLead(id)}
          />
        )}
      </div>

      {selectedLead && (
        <LeadDetailModal
          leadId={selectedLead}
          stages={stages}
          users={[]}
          isAdmin={isAdmin}
          onClose={() => setSelectedLead(null)}
          onUpdated={fetchData}
        />
      )}
    </AppShell>
  );
}
